/**
 * Easter story backgrounds — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterStoryBackgroundsPack: ToilePack = {
    slug: 'easter-story-backgrounds',
    category: 'season',
    name: 'Easter story backgrounds',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'blossom', label: 'Blossom frame', subject: 'A tall background with blossom branches framing the edges and a calm centre for text'},
        {key: 'meadow', label: 'Meadow', subject: 'A tall background of a spring meadow with eggs at the bottom, a calm sky above for text'},
        {key: 'panel', label: 'Pattern panel', subject: 'A tall background of an easter pattern with a large blank panel in the middle for text'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['with a brighter spring sky', 'as a landscape banner instead', 'with a smaller reserved panel'],
    directionHint: 'try: with a brighter spring sky',
    seo: {
        title: 'Easter Story Backgrounds: Blossom, Meadow & Panel',
        metaDescription: 'Nine Easter Instagram story backgrounds — blossom frame, spring meadow, pattern panel — in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter Instagram story background: blossom frame, spring meadow, pattern panel',
        tagline: 'Nine backgrounds, three layouts, three treatments each.',
        intro: 'Nine tall Easter backgrounds sized for a story: blossom branches framing a calm centre, a spring meadow with eggs at the bottom and open sky above, and an Easter pattern with a blank panel in the middle — each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment, with no text baked in so yours reads clean on top.',
        useCases: [
            'an Instagram or Facebook story background',
            'a phone lock-screen wallpaper',
            'a digital invitation backdrop',
            'a printed backdrop for a photo table',
        ],
        faq: [
            {q: 'Is there room left for me to add my own text?', a: 'Yes — every design reserves a calm, uncluttered area specifically so your own caption or sticker text stays legible on top.'},
            {q: 'What size are these built for?', a: 'A tall vertical format matching Instagram and Facebook story dimensions, so they fill the screen without cropping.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use one of these as a phone wallpaper instead?', a: 'Yes — the tall format works well as a lock-screen or home-screen wallpaper, not just a story post.'},
        ],
    },
}
